import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters, internalPath } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import usePackWallets from "/imports/hooks/usePackWallets";
import { saveResumeIntent } from "/imports/libs/resumeIntent";
import { packMemberPricing, memberPriceNote } from "/imports/libs/memberPricing";
import { formatExpiry, formatMembershipDuration, variantRowKey } from "./packagesHelpers";

export default function usePackages() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, locations, services, sports } = useWidget();
  const { isAuthenticated, activeClientId } = useAuth();
  const { filters, setFilter } = useUrlFilters({});
  // Set when a class session sent the client here to buy the pack that pays for it.
  const joinAfter = filters.joinAfter || null;
  // Set when a slot search sent them instead. A lesson search has no booking to
  // return to — the time is still to be picked — so the way back is the search
  // itself, filters and day intact.
  const backTo = internalPath(filters.from);

  const { data, loading, error } = useFetch(() => api.listPackagesForSale(slug), [slug], { skip: !slug });
  const items = data?.items || [];

  const { data: membershipData, loading: membershipsLoading } = useFetch(() => api.listMembershipsForSale(slug), [slug], { skip: !slug });
  const memberships = membershipData?.items || [];

  // The session that sent the client here, named so the way back says which
  // class it leads to. Carried in the URL rather than storage, so it survives a
  // refresh, a new tab, or the link being opened on another device.
  const { data: joinAfterClass } = useFetch(() => api.getClassDetail(slug, joinAfter), [slug, joinAfter, activeClientId], {
    skip: !slug || !joinAfter
  });
  const goToJoinAfter = () => navigate(`/${slug}/classes/${joinAfter}`);

  // Where the client's member pricing reaches drives which price is shown as
  // the headline number. The server is the authority on whether member pricing
  // actually applies at purchase time — this is purely a UI signal.
  const { data: memberStatus } = useFetch(() => api.getPurchaseMembershipStatus(slug), [slug, activeClientId], { skip: !slug || !isAuthenticated });

  // Plans the client already owns — matched onto the catalog rows below so a
  // row the client has bought shows its remaining sessions inline. Expired
  // ones stay in the dashboard's history; here only current plans matter.
  const { data: ownedData } = useFetch(() => api.getUserActivity(slug, { tab: "packages", page: 1, limit: 50 }), [slug, activeClientId], {
    skip: !slug || !isAuthenticated
  });
  const ownedPlans = useMemo(() => (ownedData?.items || []).filter((p) => p.status !== "expired"), [ownedData]);

  // What the client already owns of a catalog package variant. An owned V2
  // sale records its variant (sessionDuration + linkedServiceId); when either
  // side lacks variant data (legacy sales) the match falls back to the
  // product. Same-type purchases aggregate: sessions sum, expiry shows the
  // soonest date (the one the client can actually lose sessions to).
  const ownedForVariant = (pkg, opt) => {
    const matches = ownedPlans.filter((p) => {
      if (p.kind !== "package" || p.productId !== pkg._id) return false;
      if (p.sessionDuration != null && opt.sessionDuration != null && p.sessionDuration !== opt.sessionDuration) return false;
      if (p.linkedServiceId && opt.linkedServiceId && p.linkedServiceId !== opt.linkedServiceId) return false;
      return true;
    });
    if (matches.length === 0) return null;
    const sessionsRemaining = matches.reduce((sum, p) => sum + (p.sessionsRemaining || 0), 0);
    const noExpiry = matches.every((p) => p.noExpiry);
    const expiryDates = matches
      .map((p) => p.expiryDate)
      .filter(Boolean)
      .sort();
    return { sessionsRemaining, noExpiry, expiryDate: noExpiry ? null : expiryDates[0] || null };
  };

  const ownedMembershipFor = (membership) => {
    const match = ownedPlans.find((p) => p.kind === "membership" && p.productId === membership._id);
    if (!match) return null;
    return { noExpiry: match.noExpiry, expiryDate: match.expiryDate, status: match.status };
  };

  // Pack series the client has booked ahead. The next pack can carry each one on
  // from the day after its last session instead of starting on top of it.
  const { data: continuationData } = useFetch(() => api.listPackContinuations(slug), [slug, activeClientId], {
    skip: !slug || !isAuthenticated
  });

  // Set when a booking page sent the client here to buy a specific pack.
  const highlightKey = filters.highlight || null;
  // ...and the client's way out of that one-pack view, into the whole catalogue.
  const [showAllPlans, setShowAllPlans] = useState(false);

  const [buyingKey, setBuyingKey] = useState(null);
  // Variant key whose inline location picker is currently open.
  const [expandedKey, setExpandedKey] = useState(null);
  // Pending purchase awaiting confirmation: { name, lines, location, price, run }.
  const [confirm, setConfirm] = useState(null);

  // Flatten every package's variants into one list so each row is a full-width
  // "this many sessions for this price" card the client can act on directly.
  const variants = items.flatMap((pkg) =>
    (pkg.pricingOptions || []).map((opt) => ({
      pkg,
      opt,
      key: variantRowKey(pkg, opt)
    }))
  );

  const membershipRows = memberships.flatMap((membership) =>
    (membership.pricingOptions || []).map((opt) => ({
      membership,
      opt,
      key: `m:${membership._id}:${opt.pricingId}`
    }))
  );

  const locById = useMemo(() => new Map((locations || []).map((l) => [l._id, l])), [locations]);

  // A row's member pricing, at the branch Buy will put it at (buyLocationFor) —
  // so the card never shows a price the confirm step then changes.
  const memberPricingFor = (pkg, opt) => {
    const pricing = packMemberPricing({ pkg, opt, memberStatus, purchaseLocationId: buyLocationFor(pkg).locationId });
    return { ...pricing, note: memberPriceNote({ pricing, opt, currency, nameOf: (id) => locById.get(id)?.name, withAmount: false }) };
  };

  // Packages and memberships can be tied to specific locations — let the
  // client narrow the page to one when the widget spans several branches.
  const matchesLocation = (entity) => !filters.locationId || (entity.locationIds || []).includes(filters.locationId);

  // A club with a long catalogue lists classes, court rentals and private lessons in one
  // run, and they are bought for completely different reasons. The variant already
  // carries the activity it redeems against, so the page can narrow to one.
  //
  // Memberships are deliberately not filtered: they grant ongoing access rather than
  // sessions of one activity, so a category has nothing to say about them.
  const matchesCategory = ({ opt }) => !filters.categoryType || opt.linkedCategoryType === filters.categoryType;

  // A pack's sport is the sport of the service it redeems against — a variant row of a
  // multi-service pack can be tennis while its sibling is padel. The pack's own sport is
  // only a fallback for a linked service the widget doesn't list. A pack with neither is
  // not tied to any sport, so it stays visible whichever sport is picked.
  const serviceSportById = useMemo(() => new Map((services || []).map((s) => [s._id, s.sportId])), [services]);
  const variantSportId = ({ pkg, opt }) => (opt.linkedServiceId && serviceSportById.get(opt.linkedServiceId)) || pkg.sportId || null;
  const matchesSport = (variant) => {
    if (!filters.sport) return true;
    const sportId = variantSportId(variant);
    return !sportId || sportId === filters.sport;
  };

  const locationVariants = variants.filter(({ pkg }) => matchesLocation(pkg));
  const sportVariants = locationVariants.filter(matchesSport);
  const visibleVariants = sportVariants.filter(matchesCategory);
  const visibleMembershipRows = membershipRows.filter(({ membership }) => matchesLocation(membership));

  const showLocationFilter = (locations || []).length > 1 && (variants.length > 0 || membershipRows.length > 0);

  // Only sports that something on sale at the chosen branch redeems against, in the
  // club's own sport order. One sport means there is nothing to choose between.
  const sportOptions = useMemo(() => {
    const present = new Set(locationVariants.map(variantSportId).filter(Boolean));
    return (sports || []).filter((s) => present.has(s._id)).map((s) => ({ value: s._id, label: s.name }));
  }, [locationVariants, sports, serviceSportById]);

  const showSportFilter = sportOptions.length > 1;

  // Waits for the catalogue: before it lands there are no options, and a shared
  // link's sport would be wiped before anything could match it.
  useEffect(() => {
    if (!filters.sport || loading) return;
    if (sportOptions.some((o) => o.value === filters.sport)) return;
    setFilter("sport", null);
  }, [sportOptions, filters.sport, setFilter, loading]);

  // Offered in the order a club thinks about them, and only where something is actually
  // on sale — computed AFTER the location and sport filters, so switching either never
  // leaves a category selected that has nothing behind it.
  const categoryOptions = useMemo(() => {
    // Same names as the home cards, so clients learn one word for each. "Group Classes"
    // keeps a private lesson from being read as a class. Ordered the way the nav orders them.
    const labels = [
      ["category-service-court", "Court Rentals"],
      ["category-service-private", "Private Lessons"],
      ["category-service-class", "Group Classes"]
    ];
    const present = new Set(sportVariants.map(({ opt }) => opt.linkedCategoryType).filter(Boolean));
    return labels.filter(([value]) => present.has(value)).map(([value, label]) => ({ value, label }));
  }, [sportVariants]);

  const showCategoryFilter = categoryOptions.length > 1;

  // A category that survived a location change but now has nothing behind it would leave
  // the page empty with no way to tell why. Clear it instead.
  useEffect(() => {
    if (!filters.categoryType) return;
    if (categoryOptions.some((o) => o.value === filters.categoryType)) return;
    setFilter("categoryType", null);
  }, [categoryOptions, filters.categoryType, setFilter]);

  // Login takes its redirect relative to the slug and prefixes it itself, so an
  // absolute path came back doubled — and a bare "plans" would strand a visitor
  // who arrived from a booking page, dropping both the pack advertised to them
  // and the search they were sent from.
  const requireLogin = () => {
    if (isAuthenticated) return false;
    navigate(`/${slug}/login?redirect=${encodeURIComponent(`plans${location.search}`)}`);
    return true;
  };

  // A package's locations resolved to {_id, name} — the choices a buyer picks
  // from for a per-location package.
  const packageLocations = (pkg) => (pkg.locationIds || []).map((id) => locById.get(id)).filter(Boolean);

  // Where Buy puts a package before asking anything. Shared packages need no
  // location. When the page is filtered to one location, that is the buyer's
  // choice. A package at one of this widget's branches has only one answer.
  // needsPick: a per-location package at several branches, none chosen yet.
  const buyLocationFor = (pkg) => {
    if (pkg.crossLocationMode === "shared") return { locationId: null, needsPick: false };
    if (filters.locationId) return { locationId: filters.locationId, needsPick: false };
    const locs = packageLocations(pkg);
    if (locs.length <= 1) return { locationId: locs[0]?._id || null, needsPick: false };
    return { locationId: null, needsPick: true };
  };

  const doPurchase = async (pkg, opt, locationId, { voucherId } = {}) => {
    const key = `${pkg._id}:${opt.variantKey || opt.pricingId}`;
    setBuyingKey(locationId ? `${key}@@${locationId}` : key);
    try {
      const result = await api.purchasePackage(slug, {
        productId: pkg._id,
        pricingId: opt.pricingId,
        variantKey: opt.variantKey,
        sessions: opt.sessions,
        sessionDuration: opt.sessionDuration,
        linkedServiceId: opt.linkedServiceId,
        locationId: locationId || undefined,
        voucherId: voucherId || undefined
      });
      // Sent here from a session the client wants this pack to pay for. The
      // card payment leaves the app, so the way back is stashed before the hop;
      // a pack settled without one (wallet, voucher, zero price) can go straight
      // back to the session, where the new pack is waiting at checkout.
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        if (joinAfter || backTo) saveResumeIntent({ slug, joinAfter, backTo });
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete", { variant: "success" });
      navigate(joinAfter ? `/${slug}/classes/${joinAfter}` : backTo || `/${slug}/dashboard?tab=packages`);
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuyingKey(null);
    }
  };

  const doPurchaseMembership = async (membership, opt) => {
    setBuyingKey(`m:${membership._id}:${opt.pricingId}`);
    try {
      const result = await api.purchaseMembership(slug, {
        productId: membership._id,
        pricingId: opt.pricingId
      });
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete", { variant: "success" });
      navigate(`/${slug}/dashboard`);
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuyingKey(null);
    }
  };

  // Open the confirmation modal for a package variant at a resolved location.
  const openPackageConfirm = (pkg, opt, locationId) => {
    // Upfront variants book their class sessions as part of the purchase — the
    // picks happen on a page of their own, which runs the confirm + purchase
    // itself. The location is resolved HERE (shared/single/picked) because the
    // picker needs it to list the right occurrences.
    if (opt.upfrontBooking === "all" || opt.upfrontBooking === "partial") {
      setExpandedKey(null);

      // Private-lesson / court packs compose their sessions on the real
      // booking page in pack mode (?packProduct=…) — full filters, week grid,
      // multiselect + progress tray. (The standalone builder at
      // plans/select-sessions still handles these too, kept as the fallback.)
      if (["category-service-private", "category-service-court"].includes(opt.linkedCategoryType)) {
        const page = opt.linkedCategoryType === "category-service-court" ? "courts" : "lessons";
        const params = new URLSearchParams();
        params.set("packProduct", pkg._id);
        if (opt.variantKey) params.set("packVariant", opt.variantKey);
        if (locationId) params.set("packLocation", locationId);
        if (filters.from) params.set("from", filters.from);
        navigate(`/${slug}/${page}?${params.toString()}`);
        return;
      }

      const params = new URLSearchParams();
      params.set("product", pkg._id);
      if (opt.variantKey) params.set("variant", opt.variantKey);
      params.set("pricingId", opt.pricingId || "");
      if (locationId) params.set("locationId", locationId);
      // Came from a class card: preselect that session in the picker.
      if (joinAfter) params.set("preselect", joinAfter);
      if (filters.from) params.set("from", filters.from);
      navigate(`/${slug}/plans/select-sessions?${params.toString()}`);
      return;
    }
    const { price: effectivePrice, regularPrice } = packMemberPricing({ pkg, opt, memberStatus, purchaseLocationId: locationId });
    const parts = [opt.sessions ? `${opt.sessions} session${opt.sessions === 1 ? "" : "s"}` : "Package"];
    if (opt.sessionDuration) parts.push(`${opt.sessionDuration} min each`);
    const expiry = formatExpiry(opt);
    if (expiry) parts.push(expiry);
    setExpandedKey(null);
    setConfirm({
      name: pkg.name,
      lines: [parts.join(" · ")],
      location: locationId ? locById.get(locationId)?.name || null : null,
      price: effectivePrice,
      regularPrice,
      walletLocationId: locationId,
      isPackage: true,
      run: (choice) => doPurchase(pkg, opt, locationId, choice)
    });
  };

  // A per-location package at several branches must have its location picked.
  const handleBuy = (pkg, opt) => {
    if (requireLogin()) return;
    const { locationId, needsPick } = buyLocationFor(pkg);
    if (!needsPick) return openPackageConfirm(pkg, opt, locationId);
    const key = `${pkg._id}:${opt.variantKey || opt.pricingId}`;
    setExpandedKey((cur) => (cur === key ? null : key));
  };

  const handleBuyMembership = (membership, opt) => {
    if (requireLogin()) return;
    const duration = formatMembershipDuration(opt);
    setConfirm({
      name: membership.name,
      lines: duration ? [duration] : [],
      location: null,
      price: parseFloat(opt.price) || 0,
      run: () => doPurchaseMembership(membership, opt)
    });
  };

  // Sent here from a booking page to buy one advertised pack. Everything else the
  // club sells is noise at that moment — the client came to answer one question,
  // and a full catalogue makes them find the answer again. Matched against every
  // variant rather than the filtered list: the link carries no filters, and a
  // location the client happened to leave selected must not hide the pack they
  // were sent for. An unmatched key (pack withdrawn, stale link) falls through to
  // the full list rather than emptying the page.
  const focusedVariant = useMemo(
    () => (highlightKey && !showAllPlans ? variants.find((v) => v.key === highlightKey) || null : null),
    [variants, highlightKey, showAllPlans]
  );

  // Each series paired with the rows on sale that continue it. A series none of
  // whose packs is on sale here has nothing to offer, so it is not shown.
  const continuations = useMemo(
    () =>
      (continuationData?.items || [])
        .map((series) => ({
          ...series,
          key: `${series.linkedServiceId}|${series.sessionDuration ?? ""}`,
          variants: variants.filter(({ pkg, opt }) => series.options.some((o) => o.productId === pkg._id && o.variantKey === opt.variantKey))
        }))
        .filter((series) => series.variants.length > 0),
    [continuationData, variants]
  );

  // The series the client chose to continue: the page narrows to the packs that
  // carry it on. With only one, there is nothing to choose and buying starts at once.
  const [continuingKey, setContinuingKey] = useState(null);
  const continuing = continuingKey ? continuations.find((s) => s.key === continuingKey) || null : null;
  const continueSeries = (series) => {
    setContinuingKey(series.key);
    if (series.variants.length === 1) handleBuy(series.variants[0].pkg, series.variants[0].opt);
  };

  // Packages only — memberships keep the card-only confirm for now.
  const wallets = usePackWallets({ open: !!confirm?.isPackage, locationId: confirm?.walletLocationId, price: confirm?.price });

  const nothingToShow = !focusedVariant && !continuing && visibleVariants.length === 0 && visibleMembershipRows.length === 0;

  return {
    navigate,
    slug,
    currency,
    locations,
    filters,
    setFilter,
    loading,
    membershipsLoading,
    error,
    memberPricingFor,
    locById,
    buyingKey,
    expandedKey,
    setExpandedKey,
    confirm,
    setConfirm,
    wallets,
    visibleVariants,
    visibleMembershipRows,
    ownedForVariant,
    ownedMembershipFor,
    showLocationFilter,
    sportOptions,
    showSportFilter,
    categoryOptions,
    showCategoryFilter,
    nothingToShow,
    highlightKey,
    focusedVariant,
    setShowAllPlans,
    joinAfter,
    joinAfterClass,
    goToJoinAfter,
    backTo,
    goBackTo: () => navigate(backTo),
    packageLocations,
    handleBuy,
    handleBuyMembership,
    openPackageConfirm,
    continuations,
    continuing,
    continueSeries,
    stopContinuing: () => setContinuingKey(null)
  };
}
